import type { ListingActivationResult } from '~/features/employer/billing/types';
import { MsaasDetailSummary } from '~/types/hlx/msaasDetail';
import { MsaasJobListing } from '~/types/hlx/msaasJobListing';
import { deleteData, getData, patchData, postData } from '~/utils/service';
import { previewListingFee } from '../listingFee';
import { OnGetSummary, Scope } from './types';

const ROUTE_NAME = 'msaasListing';
const ACTIVATE_ROUTE_NAME = 'msaasListingActivate';
const DEACTIVATE_ROUTE_NAME = 'msaasListingDeactivate';
const CANCEL_ROUTE_NAME = 'msaasListingCancel';
const RELIST_ROUTE_NAME = 'msaasListingRelist';
const SUMMARY_ROUTE_NAME = 'msaasDetailSummary';

export const API_CALLS = {
  get: async ({ input }: { input: Scope }) => {
    const employerId = input.context.employerUser?.employerId;
    return await getData(ROUTE_NAME, {
      pathParams: { employerId: employerId?.toString() || '' },
    });
  },
  delete: async ({ input }: { input: Scope }) => {
    return await deleteData(ROUTE_NAME, {
      pathParams: { id: input.context.idToDelete.toString() },
    });
  },
};

export const submitListing = async ({ input }: { input: Scope }) => {
  return await patchData<ListingActivationResult>(ACTIVATE_ROUTE_NAME, {
    pathParams: { id: input.context.listing?.id.toString() || '' },
  });
};

// [MCV-1933] What publishing this listing would cost; null when the preview could not be read.
export const previewActivationFee = async ({
  input,
  signal,
}: {
  input: Scope;
  signal?: AbortSignal;
}) => previewListingFee('msaas', input.context.listing?.id, signal);

export const deactivateListing = async ({ input }: { input: Scope }) => {
  return await patchData(DEACTIVATE_ROUTE_NAME, {
    pathParams: { id: input.context.listing?.id.toString() || '' },
  });
};

/**
 * [MCV-1587] Deactivate by row id rather than by the wizard's loaded listing.
 *
 * Deliberately a second function rather than a parameter on the one above.
 * `context.listing` is undefined in $_VIEW (initial context, and `closeModal`
 * re-nulls it), so the row path would send an empty id -- and assigning
 * `context.listing` from the clicked row to compensate is worse: a failed
 * deactivate latches it, and BasicInfoForm branches create-vs-update on
 * `listing?.id`, so the next "Create New Listing" would PATCH the row the
 * user just tried to deactivate.
 */
export const deactivateListingById = async ({ input }: { input: Scope }) => {
  return await patchData(DEACTIVATE_ROUTE_NAME, {
    pathParams: { id: input.context.idToDeactivate.toString() },
  });
};

// [MCV-1889] Cancel a hand-filled listing (409 CONTRACT_EXISTS when a contract filled it).
export const cancelListingById = async ({ input }: { input: Scope }) => {
  return await postData(CANCEL_ROUTE_NAME, {
    pathParams: { id: input.context.idToCancel.toString() },
  });
};

// [MCV-1889] Copy a cancelled listing to a new draft (201 with the draft).
export const relistListingById = async ({ input }: { input: Scope }) => {
  return await postData<MsaasJobListing>(RELIST_ROUTE_NAME, {
    pathParams: { id: input.context.idToRelist.toString() },
  });
};

export const SUMMARY_ROUTES = {
  // Get summary based on Listing
  post: async ({ input }: { input: Scope }) => {
    const listing = input.context.listing;
    return await postData(SUMMARY_ROUTE_NAME, {
      data: listing,
    });
  },
};

export const getSummary = async ({
  input,
}: {
  input: Scope;
}): Promise<MsaasDetailSummary | undefined> => {
  const listing = (input.event as OnGetSummary).payload.listing;
  if (!listing) {
    return undefined;
  }
  return await postData('msaasDetailSummary', {
    data: listing.detail,
  });
};
